import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Briefcase, MapPin, Buildings, ArrowLeft, ArrowRight, CheckCircle, Sparkle } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

// -----------------------------------------------------------------------
// Landing (list jobs for a tenant slug — default "acme")
// -----------------------------------------------------------------------
export function CareersLanding() {
  const [params] = useSearchParams();
  const slug = params.get("company") || "acme";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BASE}/api/ats/public/tenants/${slug}/jobs`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen grid place-items-center bg-adb-navy text-white">Loading…</div>;
  if (!data) return <div className="min-h-screen grid place-items-center bg-adb-navy text-white">Careers page not available.</div>;

  return (
    <div className="min-h-screen bg-white text-adb-navy">
      <header className="bg-gradient-to-br from-adb-navy via-adb-navy-800 to-adb-navy text-white">
        <div className="max-w-6xl mx-auto px-6 py-10 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-zenith-teal font-bold">Careers</div>
            <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight mt-1">Join {data.tenant.name}.</h1>
            <p className="text-sm text-white/70 mt-3 max-w-xl">We're building the future. If you love craft, curiosity and shipping fast — you'll fit right in.</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-white/60">
            <Sparkle size={14} weight="fill" className="text-zenith-magenta" />
            Powered by Zenith360
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-3xl font-bold">Open roles</h2>
          <div className="text-sm text-zinc-500">{data.jobs.length} openings</div>
        </div>
        {data.jobs.length === 0 ? (
          <div className="border border-dashed border-zinc-200 bg-zinc-50 rounded-sm p-12 text-center">
            <Briefcase size={28} weight="duotone" className="mx-auto text-zinc-300" />
            <div className="mt-2 font-medium text-zinc-600">No open roles at the moment — check back soon.</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {data.jobs.map((j) => (
              <Link key={j.id} to={`/careers/apply/${j.id}`} data-testid={`careers-job-${j.id}`}
                    className="group border border-zinc-200 rounded-sm bg-white p-5 hover:border-adb-navy transition-colors block">
                <div className="text-xs uppercase tracking-widest text-zenith-magenta font-bold">{j.department || "General"}</div>
                <div className="font-display text-xl font-bold text-adb-navy mt-1 group-hover:text-zenith-magenta">{j.title}</div>
                <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-2">
                  {j.location && <span className="inline-flex items-center gap-1"><MapPin size={11} weight="duotone" /> {j.location}</span>}
                  <span className="inline-flex items-center gap-1"><Buildings size={11} weight="duotone" /> {j.employment_type}</span>
                  <span className="inline-flex items-center gap-1">{j.openings} opening{j.openings !== 1 ? "s" : ""}</span>
                </div>
                <div className="text-xs text-zinc-600 mt-2 line-clamp-2">{j.description}</div>
                <div className="inline-flex items-center gap-1 text-xs font-bold text-adb-navy mt-3 group-hover:text-zenith-magenta">
                  Apply now <ArrowRight size={12} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-zinc-200 mt-16 py-6 text-center text-xs text-zinc-500">
        Careers page powered by <span className="text-zenith-magenta font-bold">Zenith360 HR</span> · Innovate to evolve.
      </footer>
    </div>
  );
}


// -----------------------------------------------------------------------
// Apply page
// -----------------------------------------------------------------------
export function CareersApply() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", cover_letter: "", source: "portal" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${BASE}/api/ats/public/jobs/${jobId}`).then((r) => setJob(r.data)).catch(() => setJob(null));
  }, [jobId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@")) { toast.error("Name + valid email required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      if (file) fd.append("resume", file);
      await axios.post(`${BASE}/api/ats/public/apply/${jobId}`, fd);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Application failed");
    }
    setBusy(false);
  };

  if (!job) return <div className="min-h-screen grid place-items-center bg-white">Loading…</div>;
  const j = job.job;

  return (
    <div className="min-h-screen bg-white text-adb-navy">
      <header className="bg-adb-navy text-white py-6">
        <div className="max-w-3xl mx-auto px-6">
          <button onClick={() => nav(`/careers?company=${job.tenant.slug}`)} className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white">
            <ArrowLeft size={12} /> All roles
          </button>
          <div className="font-display text-3xl md:text-4xl font-bold mt-2">{j.title}</div>
          <div className="text-sm text-white/70 mt-1">{job.tenant.name} · {j.location || "Remote"} · {j.employment_type}</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="prose prose-sm whitespace-pre-wrap max-w-none border-b border-zinc-100 pb-6 mb-6">{j.description}</div>

        {done ? (
          <div data-testid="apply-success" className="border border-emerald-200 bg-emerald-50 rounded-sm p-6 text-center">
            <CheckCircle size={40} weight="fill" className="mx-auto text-emerald-600" />
            <div className="font-display text-xl font-bold mt-2">Application received</div>
            <div className="text-sm text-zinc-600 mt-1">We'll be in touch soon — thanks for applying!</div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="font-display text-2xl font-bold">Apply for this role</div>
            <div><Label className="text-xs">Full name *</Label>
              <Input data-testid="apply-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Email *</Label>
                <Input data-testid="apply-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value.toLowerCase() })} className="rounded-sm mt-1" /></div>
              <div><Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded-sm mt-1 font-mono-plex" /></div>
            </div>
            <div><Label className="text-xs">Source</Label>
              <select data-testid="apply-source" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} className="w-full rounded-sm mt-1 border border-zinc-200 h-9 text-sm px-2">
                {["portal", "linkedin", "naukri", "referral", "instagram", "other"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Résumé (PDF or DOCX, max 5 MB)</Label>
              <input data-testid="apply-resume" type="file" accept=".pdf,.doc,.docx,application/pdf" onChange={e => setFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-xs" /></div>
            <div><Label className="text-xs">Cover letter</Label>
              <Textarea rows={5} value={form.cover_letter} onChange={e => setForm({ ...form, cover_letter: e.target.value })} className="rounded-sm mt-1" placeholder="Tell us why you'd be a great fit…" /></div>
            <Button type="submit" data-testid="apply-submit" disabled={busy} className="rounded-sm bg-adb-navy hover:bg-adb-navy-800 w-full">
              {busy ? "Submitting…" : "Submit application"}
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}


// -----------------------------------------------------------------------
// Offer acceptance page (e-signature via click + timestamp)
// -----------------------------------------------------------------------
export function AcceptOffer() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    axios.get(`${BASE}/api/ats/public/offers/${token}`).then((r) => {
      setData(r.data);
      if (r.data.offer.accepted_at) setAccepted(true);
    }).catch(() => setNotFound(true));
  }, [token]);

  const accept = async () => {
    if (!confirmed) { toast.error("Please tick the confirmation first"); return; }
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/ats/public/offers/${token}/accept`);
      setAccepted(true);
      toast.success("Offer accepted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  if (notFound) return (
    <div className="min-h-screen grid place-items-center bg-adb-navy text-white px-4">
      <div data-testid="offer-not-found" className="max-w-md w-full bg-white text-adb-navy rounded-sm p-8 text-center">
        <div className="text-xs uppercase tracking-[0.25em] text-zenith-magenta font-bold">Zenith360 HR</div>
        <h1 className="font-display text-2xl font-bold mt-2">This offer link is not valid</h1>
        <p className="text-sm text-zinc-500 mt-2">The offer may have expired, been revoked, or the URL was mistyped. Please contact your recruiter for a fresh link.</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen grid place-items-center bg-white">Loading…</div>;
  const o = data.offer;

  return (
    <div className="min-h-screen bg-adb-navy text-white grid place-items-center px-4">
      <div className="max-w-lg w-full bg-white text-adb-navy rounded-sm p-8">
        <div className="text-xs uppercase tracking-[0.25em] text-zenith-magenta font-bold">Zenith360 HR</div>
        <h1 className="font-display text-3xl font-bold mt-1">Your offer of employment</h1>
        <div className="mt-4 space-y-1 text-sm">
          <div><span className="text-zinc-500">Name:</span> {data.candidate.name}</div>
          <div><span className="text-zinc-500">Role:</span> {o.designation}</div>
          <div><span className="text-zinc-500">CTC:</span> INR {o.ctc_lpa} LPA</div>
          <div><span className="text-zinc-500">Joining date:</span> {o.joining_date}</div>
          <div><span className="text-zinc-500">Location:</span> {o.location || "TBD"}</div>
        </div>
        <a href={`${BASE}/api/ats/offers/${o.id}/pdf`} target="_blank" rel="noreferrer" data-testid="offer-download"
           className="inline-flex items-center gap-1 text-xs border border-adb-navy text-adb-navy px-2 py-1 rounded-sm hover:bg-adb-navy hover:text-white mt-4">
          Download offer letter (PDF)
        </a>

        {accepted ? (
          <div className="mt-6 border border-emerald-300 bg-emerald-50 rounded-sm p-4 flex items-center gap-2">
            <CheckCircle size={18} weight="fill" className="text-emerald-600" />
            <div>
              <div className="font-semibold text-emerald-800">Accepted</div>
              <div className="text-[11px] text-emerald-700">on {o.accepted_at?.slice(0, 19).replace("T", " ")}</div>
            </div>
          </div>
        ) : (
          <div className="mt-6 border border-zinc-200 rounded-sm p-4">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="mt-1" data-testid="offer-confirm" />
              <span>I have read and agree to the terms of this offer of employment. Clicking "Accept" below constitutes my electronic signature.</span>
            </label>
            <Button onClick={accept} disabled={busy || !confirmed} data-testid="offer-accept" className="mt-4 w-full rounded-sm bg-adb-navy hover:bg-adb-navy-800">
              {busy ? "Submitting…" : "Accept offer"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
